import React, { useEffect } from "react";

import { Dialog } from "primereact";
import Lottie from "react-lottie";

import { Spinner } from "react-bootstrap";
import BackButtom from "../../../../components/backButton";
import ResumeFiscalNote from "../../../../components/resume";

import { DeveloperCompanyModel } from "../../../../../domain/models/developer-company.model";

import { theme } from "../../../../styles/theme";
import { CardContent, Wrapper } from "./styles";

import * as animationData from "../../../../assets/animations/loading.json";

import { NFSeTypeEnum } from "../../../../enums/nfs-e-type.enum";
import { FiscalNoteModel, RecurrenceMonthlyStatus } from "../../../../../domain/models/fiscal-note";
import { Factory } from "./factory";
import ChooseModal from "../../../../components/modals/choose-modal";
import { formatNationalTaxCode } from "../../../../../utils/tax";
import { showErrorMessage } from "../../../../helpers";

type PageProps = {
  handleSubmit: (values: any, setShowConfirm: any) => Promise<void>;
  developerCompaniesList: DeveloperCompanyModel[];
  submitting?: boolean;
  fiscalNote?: FiscalNoteModel
  isToCreate: boolean;
  monthlyStatus?: RecurrenceMonthlyStatus | null;
};

const isSmallScreen = window.innerWidth < 768;

const IssueForm: React.FC<PageProps> = ({
  handleSubmit,
  developerCompaniesList,
  submitting,
  fiscalNote,
  isToCreate,
  monthlyStatus,
}) => {
  const [currentNote, setCurrentNote] = React.useState<any>(fiscalNote);
  const [confirmMutation, setConfirmMutation] = React.useState<FiscalNoteModel | undefined>();
  const [showResume, setShowResume] = React.useState<boolean>(false);
  const [showConfirm, setShowConfirm] = React.useState<boolean>(false);
  const [loadingNote, setLoadingNote] = React.useState<boolean>(!isToCreate);

  useEffect(() => {
    if (fiscalNote && !isToCreate) {
      const service = fiscalNote.serviceList?.[0];
      setCurrentNote({
        ...fiscalNote,
        company: fiscalNote.developerCompany?.id,
        cnae: service?.cnae?.id,
        nationalService: service?.nationalService?.id,
        nbs: service?.nbs?.id,
        iss: service?.issRetained ? "true" : "false",
        cnaeLabel: service?.cnae ? `${service.cnae.code} - ${service.cnae.description}` : "",
        nationalServiceLabel: service?.nationalService ? `${formatNationalTaxCode(service.nationalService.nationalTaxCode || "")} - ${service.nationalService.description}` : "",
        nbsLabel: service?.nbs ? `${service.nbs.codigoNbs} - ${service.nbs.description}` : "",
        issAliquot: service?.issAliquot?.toString().replace(".", ",") || "0",
        // Dados de obra
        obraTipo: service?.obraTipo || "",
        obraCodigo: service?.obraCodigo || "",
        obraInscricaoImobFisc: service?.obraInscricaoImobFisc || "",
        obraAddress: service?.obraAddress || undefined,
      });
    }
  }, [fiscalNote, isToCreate]);

  const findCompanyById = (companies: any[], id: string): any =>
    companies.find((item) => item.id === id)?.company;

  const getUpdatedNoteData = (
    values: any,
    selectedCompany: any,
    cnae: any,
  ): any => ({
    ...values,
    emissionDate: new Date().toISOString(),
    simplesNacional: selectedCompany?.simpleNacional,
    subscriptionNumber: selectedCompany?.subscriptionNumber || "",
    address: selectedCompany?.address || "",
    cnae,
    hasNationalFiscal: values.nfType === NFSeTypeEnum.NFS_E_NACIONAL,
  });

  const handleAddNote = async (values: any, actions: any): Promise<void> => {
    try {
      const selectedCompany = findCompanyById(developerCompaniesList, values.company);
      const selectedCnae = selectedCompany?.cnaes?.find((el: any) => el.id === values.cnae);

       const certificate = selectedCompany?.certificate;
      if (!certificate?.id && !certificate?.url) {
        showErrorMessage(
          "A empresa selecionada não possui um certificado digital válido. Para emitir nota fiscal, por favor, cadastre um certificado em Perfil -> Empresa.",
        );
        actions.setSubmitting(false);
        return;
      }

      const updatedNote = getUpdatedNoteData(values, selectedCompany, selectedCnae);

      setCurrentNote((prev: any) => ({ ...prev, ...updatedNote }));
      setShowResume(true);
    } catch (error) {
      console.error("Erro ao submeter o formulário:", error);
    } finally {
      actions.setSubmitting(false);
    }
  };

  if (
    (!isToCreate && !fiscalNote)
    || !developerCompaniesList
    || developerCompaniesList.length === 0
  ) {
    return (
      <div className="d-flex justify-content-center">
        <Spinner animation="grow" variant="primary" />
      </div>
    );
  }

  const getDayOfRecurrence = (dateString: string): string => {
    if (!dateString) return "";
    const date = new Date(dateString).getDate() + 1;
    return date.toString().padStart(2, "0");
  };

  const getWarningMessage = (): string => {
    const defaultMsg = `A nota recorrente será emitida todo dia ${getDayOfRecurrence(currentNote?.dayOfRecurrence)} dos meses subsequentes. Deseja continuar?`;

    if (isToCreate || !monthlyStatus?.hasNoteThisMonth) {
      return defaultMsg;
    }

    const today = new Date();
    const newDate = currentNote?.dayOfRecurrence ? new Date(currentNote.dayOfRecurrence) : null;
    const originalDate = fiscalNote?.dayOfRecurrence ? new Date(fiscalNote.dayOfRecurrence) : null;

    const dateChanged = newDate && originalDate
      && newDate.toISOString().split("T")[0] !== originalDate.toISOString().split("T")[0];

    const lastDateFormatted = monthlyStatus.lastNoteDate
      ? new Date(monthlyStatus.lastNoteDate).toLocaleDateString("pt-BR")
      : "";

    const monthName = today.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });

    if (dateChanged && newDate) {
      const isSameMonth = newDate.getFullYear() === today.getFullYear()
        && newDate.getMonth() === today.getMonth()
        && newDate > today;

      if (isSameMonth) {
        return `Já existe uma nota gerada para ${monthName} (emitida em ${lastDateFormatted}). Com a data alterada para ${newDate.toLocaleDateString("pt-BR")}, será gerada uma nova nota ainda neste mês. Deseja continuar?`;
      }
    }

    return `Já existe uma nota gerada para ${monthName}. As alterações valerão a partir das próximas emissões. Deseja continuar?`;
  };

  return (
    <Wrapper>
      <BackButtom text={isToCreate ? "Nova Recorrência" : "Atualizar Recorrência"} />
      <CardContent style={{ position: "relative" }}>
        {loadingNote && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.4)",
              zIndex: 9999,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "8px",
            }}
          >
            <Lottie
              options={{
                loop: true,
                autoplay: true,
                animationData,
                rendererSettings: {
                  preserveAspectRatio: "xMidYMid slice",
                },
              }}
              height={50}
              width={150}
            />
          </div>
        )}
        <Factory
          fiscalNote={fiscalNote}
          developerCompaniesList={developerCompaniesList}
          handleAddNote={handleAddNote}
          currentNote={currentNote}
          setCurrentNote={setCurrentNote}
          isToCreate={isToCreate}
          loadingNote={loadingNote}
          setIsLoadingNote={setLoadingNote}
        />
      </CardContent>
      <Dialog
        header="Resumo da Recorrência:"
        visible={showResume}
        style={{
          width: window.innerWidth < 479 ? "90vw" : "50vw",
          backgroundColor: theme.colors.darkBlue,
        }}
        headerStyle={{
          backgroundColor: theme.colors.darkBlue,
          color: theme.colors.white,
        }}
        contentStyle={{
          backgroundColor: theme.colors.darkBlue,
        }}
        footer={null}
        onHide={() => setShowResume(false)}
      >
        <ResumeFiscalNote
          submitting={submitting}
          fiscalNote={{
            ...currentNote,
            hasRecurrence: "true",
          }}
          onConfirmCallback={async () => {
            setConfirmMutation(currentNote);
          }}
          onCancelCallback={() => setShowResume(false)}
          titleConfirmButton={isToCreate ? "Confirmar e Criar Recorrência" : "Confirmar e Atualizar Recorrência"}
        />
      </Dialog>
      <ChooseModal
        text={getWarningMessage()}
        onConfirm={async () => {
          setConfirmMutation(undefined);
          await handleSubmit(currentNote, setShowConfirm);
        }}
        onCancel={() => {
          setConfirmMutation(undefined);
        }}
        isVisible={!!confirmMutation}
        toggleVisibility={() => setConfirmMutation(undefined)}
      />
      <Dialog
        header="Aguarde!"
        visible={showConfirm}
        headerStyle={{
          backgroundColor: theme.colors.darkBlue,
          color: theme.colors.white,
          fontFamily: theme.font.poppins,
        }}
        style={{
          width: isSmallScreen ? "90vw" : "35vw",
          backgroundColor: theme.colors.darkBlue,
          color: theme.colors.white,
          fontFamily: theme.font.poppins,
        }}
        contentStyle={{
          backgroundColor: theme.colors.darkBlue,
          color: theme.colors.white,
          fontFamily: theme.font.poppins,
        }}
        footer={null}
        onHide={() => null}
        closable={false}
      >
        <Lottie
          options={{
            loop: true,
            autoplay: true,
            animationData,
            rendererSettings: {
              preserveAspectRatio: "xMidYMid slice",
            },
          }}
          height={20}
          width={100}
          isStopped={false}
          isPaused={false}
        />
        <p className="text-center mt-4" style={{ maxWidth: "100%" }}>
          Nota recorrente sendo gerada. Você será redirecionado para tela de
          listagem onde poderá editar ou excluir.
        </p>
      </Dialog>
    </Wrapper>
  );
};
export default IssueForm;
